<template>
    <div class="rd">
        <div class="pagina">
            <header class="paginakop">
                <div class="tekst">
                    <h1>Opening hours</h1>
                    <p>
                        Set the weekly hours. Close the location for a holiday or part of a day.
                        Extra opening replaces the weekly hours on the dates you pick. Existing
                        bookings stay on the calendar.
                    </p>
                </div>
            </header>

            <section v-if="locations.length > 1" class="paneel filters">
                <div class="filter-rij">
                    <div class="veld f-loc">
                        <label for="oh-loc">Location</label>
                        <select id="oh-loc" v-model="location">
                            <option v-for="loc in locations" :key="loc.id" :value="loc">{{ loc.name }}</option>
                        </select>
                    </div>
                </div>
            </section>

            <p v-if="!locationsLoading && locations.length === 0" class="rd-laden">You have no locations.</p>
            <p v-else-if="loading" class="rd-laden">Loading opening hours...</p>

            <template v-if="location && !loading">
                <!-- Weekly hours -->
                <section class="paneel">
                    <div class="paneel-kop"><h2>Weekly hours</h2></div>
                    <p v-if="pastMidnight" class="hint">A closing time earlier than the opening time stays open past midnight.</p>
                    <p v-if="extras.length" class="hint">Some days are stored more than once. Bookings use the oldest row until you remove the extras and save.</p>
                    <div class="uren-week">
                        <div v-for="day in days" :key="day.day" class="uren-dag" :class="{ dicht: !day.open }">
                            <span class="dag-naam">{{ day.day }}</span>
                            <label class="aanvink"><input type="checkbox" v-model="day.open" /> Open</label>
                            <input type="time" v-model="day.from" :aria-label="`${day.day} from`" :disabled="!day.open" />
                            <span class="tot">–</span>
                            <input type="time" v-model="day.till" :aria-label="`${day.day} until`" :disabled="!day.open" />
                        </div>
                    </div>
                    <div v-for="extra in extras" :key="extra.id" class="uren-extra-rij">
                        <span class="dag-naam">{{ extra.day }}</span>
                        <span>{{ extra.open ? "Open" : "Closed" }}</span>
                        <span class="num">{{ hhmmToTime(extra.from) }} – {{ hhmmToTime(extra.till) }}</span>
                        <button class="btn btn-stil btn-sm" type="button" @click="removeExtra(extra.id)">Remove</button>
                    </div>
                    <div class="knoprij" style="margin-top: 20px">
                        <button class="btn btn-paars" type="button" :disabled="savingWeek || extras.length > 0" @click="saveWeek">Save weekly hours</button>
                    </div>
                </section>

                <!-- Extra opening -->
                <section class="paneel">
                    <div class="paneel-kop">
                        <div class="tekst">
                            <h2>Extra opening</h2>
                            <span class="uitleg">These dates replace the weekly hours, so a normally closed day can open.</span>
                        </div>
                    </div>
                    <div v-if="overrides.length" class="tabel-wikkel">
                        <table class="tabel">
                            <thead>
                                <tr><th>From</th><th>Until</th><th>Hours</th><th><span hidden>Actions</span></th></tr>
                            </thead>
                            <tbody>
                                <tr v-for="row in overrides" :key="row.id">
                                    <td class="nowrap">{{ formatCalendarDate(row.start_date) }}</td>
                                    <td class="nowrap">{{ formatCalendarDate(row.end_date) }}</td>
                                    <td class="num nowrap">{{ hhmmToTime(row.from) }} – {{ hhmmToTime(row.till) }}</td>
                                    <td class="rechts"><button class="btn btn-stil btn-sm" type="button" @click="removeOverride(row)">Remove</button></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p v-else class="leegwaarde" style="margin: 0">No extra opening.</p>
                    <div class="uren-form">
                        <div class="veld">
                            <label>From – until</label>
                            <date-picker
                                v-model="overrideRange"
                                type="date"
                                range
                                value-type="date"
                                format="DD-MM-YYYY"
                                range-separator=" – "
                                :clearable="false"
                                :editable="false"
                                placeholder="Pick a date range"
                                aria-label="Extra opening date range"
                            />
                        </div>
                        <div class="veld"><label for="ov-vt">Open</label><input id="ov-vt" type="time" v-model="overrideFrom" /></div>
                        <div class="veld"><label for="ov-tt">Close</label><input id="ov-tt" type="time" v-model="overrideTill" /></div>
                        <button class="btn btn-stil" type="button" :disabled="savingOverride" @click="addOverride">Add extra opening</button>
                    </div>
                </section>

                <!-- Temporarily closed -->
                <section class="paneel">
                    <div class="paneel-kop">
                        <div class="tekst">
                            <h2>Temporarily closed</h2>
                            <span class="uitleg">A closure only stops new bookings. Existing bookings stay.</span>
                        </div>
                    </div>
                    <div v-if="closures.length" class="tabel-wikkel">
                        <table class="tabel">
                            <thead>
                                <tr><th>From</th><th>Until</th><th><span hidden>Actions</span></th></tr>
                            </thead>
                            <tbody>
                                <tr v-for="row in closures" :key="row.id">
                                    <td class="nowrap">{{ formatInLocation(row.start, location.time_zone) }}</td>
                                    <td class="nowrap">{{ formatInLocation(row.end, location.time_zone) }}</td>
                                    <td class="rechts"><button class="btn btn-stil btn-sm" type="button" @click="removeClosure(row)">Remove</button></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p v-else class="leegwaarde" style="margin: 0">No closures.</p>
                    <div class="uren-form">
                        <div class="veld">
                            <label id="cl-m-l">What to close</label>
                            <div class="keuze" role="radiogroup" aria-labelledby="cl-m-l">
                                <button type="button" role="radio" :aria-pressed="closureMode === 'days'" :aria-checked="closureMode === 'days'" @click="closureMode = 'days'">Whole days</button>
                                <button type="button" role="radio" :aria-pressed="closureMode === 'part'" :aria-checked="closureMode === 'part'" @click="closureMode = 'part'">Part of a day</button>
                            </div>
                        </div>
                    </div>
                    <div v-if="closureMode === 'days'" class="uren-form">
                        <div class="veld">
                            <label>From – until</label>
                            <date-picker
                                v-model="closureRange"
                                type="date"
                                range
                                value-type="date"
                                format="DD-MM-YYYY"
                                range-separator=" – "
                                :clearable="false"
                                :editable="false"
                                placeholder="Pick a date range"
                                aria-label="Closure date range"
                            />
                        </div>
                        <button class="btn btn-stil" type="button" :disabled="savingClosure" @click="addClosure">Add closure</button>
                    </div>
                    <div v-else class="uren-form">
                        <div class="veld">
                            <label>Date</label>
                            <date-picker
                                v-model="closureDate"
                                type="date"
                                value-type="date"
                                format="DD-MM-YYYY"
                                :clearable="false"
                                :editable="false"
                                placeholder="Pick a date"
                                aria-label="Closure date"
                            />
                        </div>
                        <div class="veld"><label for="cl-vt">From</label><input id="cl-vt" type="time" v-model="closureFrom" /></div>
                        <div class="veld"><label for="cl-tt">Until</label><input id="cl-tt" type="time" v-model="closureTill" /></div>
                        <button class="btn btn-stil" type="button" :disabled="savingClosure" @click="addClosure">Add closure</button>
                    </div>
                </section>
            </template>
        </div>
    </div>
</template>

<script setup>
import { Message, MessageBox } from "element-ui";
import { computed, onMounted, ref, watch } from "vue";
import DatePicker from "vue2-datepicker";
import { getUserLocationsSimple } from "../../api/graphql/locations";
import {
    ValidationError,
    createClosure,
    createOpeningHourOverride,
    deleteClosure,
    deleteOpeningHourOverride,
    getBookingCountInRange,
    getClosures,
    getFutureBookingDates,
    getOpeningHourOverrides,
    getOpeningHours,
    replaceOpeningHours,
} from "../../api/rest";
import {
    buildWeekPayload,
    calendarDateOf,
    closureInterval,
    countBookingsOnWeekdays,
    formatCalendarDate,
    formatInLocation,
    hhmmToTime,
    operatorScheduleError,
    overridePayload,
    runsPastMidnight,
    timeToHhmm,
    weekDraftFromRows,
} from "../../helpers/openingHours";

const locations = ref([]);
const locationsLoading = ref(true);
const location = ref(null);
const loading = ref(false);
const days = ref([]);
const extras = ref([]);
const deleteIds = ref([]);
// Open-state per weekday at load / last save, to detect weekdays going Open → closed on save.
const originalOpenByDay = ref({});
const closures = ref([]);
const overrides = ref([]);
const savingWeek = ref(false);
const savingClosure = ref(false);
const savingOverride = ref(false);
const closureMode = ref("days");
// Date-picker models are Date objects (value-type="date"); converted to YYYY-MM-DD on use.
const closureRange = ref(null);
const closureDate = ref(null);
const closureFrom = ref("12:00");
const closureTill = ref("17:00");
const overrideRange = ref(null);
const overrideFrom = ref("10:00");
const overrideTill = ref("18:00");

const pastMidnight = computed(() =>
    days.value.some((day) => day.open && runsPastMidnight(timeToHhmm(day.from), timeToHhmm(day.till))),
);

const fail = (error, fallback) => {
    Message.error(
        error instanceof ValidationError ? operatorScheduleError(error.message, fallback) : fallback,
    );
};

const snapshotOpen = (daysList) => Object.fromEntries(daysList.map((day) => [day.day, day.open]));

// Warn (only when count > 0) that existing bookings sit in time that is about to close.
// They stay on the calendar; closing only blocks new bookings. Returns true to proceed,
// false when the operator cancels (save nothing).
const confirmBookingsInClosedTime = async (message, confirmButtonText) => {
    try {
        await MessageBox.confirm(message, "Bookings in closed time", {
            confirmButtonText,
            cancelButtonText: "Cancel",
            type: "warning",
        });
        return true;
    } catch {
        return false;
    }
};

const bookingsFall = (count) =>
    `${count} ${count === 1 ? "booking" : "bookings"} ${count === 1 ? "falls" : "fall"}`;

const loadSchedule = async (locationId) => {
    loading.value = true;
    try {
        const [hours, closureRows, overrideRows] = await Promise.all([
            getOpeningHours(locationId),
            getClosures(locationId),
            getOpeningHourOverrides(locationId),
        ]);
        const draft = weekDraftFromRows(hours);
        days.value = draft.days;
        extras.value = draft.extras;
        deleteIds.value = [];
        originalOpenByDay.value = snapshotOpen(draft.days);
        closures.value = closureRows;
        overrides.value = overrideRows;
    } catch (error) {
        fail(error, "Failed to load opening hours");
    } finally {
        loading.value = false;
    }
};

const removeExtra = (id) => {
    extras.value = extras.value.filter((row) => row.id !== id);
    deleteIds.value = [...deleteIds.value, id];
};

const saveWeek = async () => {
    if (extras.value.length > 0) {
        Message.error("Remove the extra rows before saving");
        return;
    }
    const missingTime = days.value.some((day) => timeToHhmm(day.from) == null || timeToHhmm(day.till) == null);
    if (missingTime) {
        Message.error("Enter a time for every day");
        return;
    }
    const sameTime = days.value.some((day) => day.open && timeToHhmm(day.from) === timeToHhmm(day.till));
    if (sameTime) {
        Message.error("Opening and closing time must differ when the day is open");
        return;
    }

    // Only weekdays switching Open → closed can orphan bookings; others never block.
    const closingWeekdays = days.value
        .filter((day) => !day.open && originalOpenByDay.value[day.day])
        .map((day) => day.day);
    if (closingWeekdays.length > 0) {
        let futureDates;
        try {
            futureDates = await getFutureBookingDates(
                location.value.id,
                new Date().toISOString(),
                "2999-01-01T00:00:00.000Z",
            );
        } catch (error) {
            fail(error, "Failed to check existing bookings");
            return;
        }
        const count = countBookingsOnWeekdays(futureDates, closingWeekdays, location.value.time_zone);
        if (count > 0) {
            const proceed = await confirmBookingsInClosedTime(
                `${bookingsFall(count)} on the day(s) you are closing. They stay on the calendar. New bookings are blocked.`,
                "Save weekly hours",
            );
            if (!proceed) {
                return;
            }
        }
    }

    savingWeek.value = true;
    try {
        const saved = await replaceOpeningHours(location.value.id, buildWeekPayload(days.value, deleteIds.value));
        const draft = weekDraftFromRows(saved);
        days.value = draft.days;
        extras.value = draft.extras;
        deleteIds.value = [];
        originalOpenByDay.value = snapshotOpen(draft.days);
        Message.success("Opening hours saved");
    } catch (error) {
        fail(error, "Failed to save opening hours");
    } finally {
        savingWeek.value = false;
    }
};

const addOverride = async () => {
    const [start, end] = overrideRange.value || [];
    if (!start || !end) {
        Message.error("Pick a date");
        return;
    }
    const body = overridePayload({
        startDate: calendarDateOf(start),
        endDate: calendarDateOf(end),
        from: overrideFrom.value,
        till: overrideTill.value,
    });
    if (!body) {
        Message.error("Opening and closing time must differ");
        return;
    }
    savingOverride.value = true;
    try {
        await createOpeningHourOverride(location.value.id, body);
        overrides.value = await getOpeningHourOverrides(location.value.id);
        overrideRange.value = null;
        Message.success("Extra opening added");
    } catch (error) {
        fail(error, "Failed to add extra opening");
    } finally {
        savingOverride.value = false;
    }
};

const removeOverride = async (row) => {
    // Removing an extra opening reverts those dates to the weekly template, which can close a
    // day that currently has bookings. Warn on any booking on the override's dates (safe to
    // over-warn: the removal might still leave the day open, but the operator decides).
    const interval = closureInterval({
        mode: "days",
        startDate: String(row.start_date).slice(0, 10),
        endDate: String(row.end_date).slice(0, 10),
        timeZone: location.value.time_zone,
    });
    let count;
    try {
        count = await getBookingCountInRange(location.value.id, interval.start, interval.end);
    } catch (error) {
        fail(error, "Failed to check existing bookings");
        return;
    }
    if (count > 0) {
        const proceed = await confirmBookingsInClosedTime(
            `${bookingsFall(count)} on these dates. They stay on the calendar. New bookings are blocked.`,
            "Remove extra opening",
        );
        if (!proceed) {
            return;
        }
    }
    try {
        await deleteOpeningHourOverride(location.value.id, row.id);
        overrides.value = overrides.value.filter((item) => item.id !== row.id);
        Message.success("Extra opening removed");
    } catch (error) {
        fail(error, "Failed to remove extra opening");
    }
};

const addClosure = async () => {
    const range = closureMode.value === "days" ? closureRange.value || [] : [closureDate.value, closureDate.value];
    if (!range[0] || !range[1]) {
        Message.error("Pick a date");
        return;
    }
    const interval = closureInterval({
        mode: closureMode.value,
        startDate: calendarDateOf(range[0]),
        endDate: calendarDateOf(range[1]),
        from: closureFrom.value,
        till: closureTill.value,
        timeZone: location.value.time_zone,
    });
    if (!interval) {
        Message.error("Opening and closing time must differ");
        return;
    }

    let count;
    try {
        count = await getBookingCountInRange(location.value.id, interval.start, interval.end);
    } catch (error) {
        fail(error, "Failed to check existing bookings");
        return;
    }
    if (count > 0) {
        const proceed = await confirmBookingsInClosedTime(
            `${bookingsFall(count)} in this period. They stay on the calendar. New bookings are blocked.`,
            "Add closure",
        );
        if (!proceed) {
            return;
        }
    }

    savingClosure.value = true;
    try {
        await createClosure(location.value.id, interval);
        closures.value = await getClosures(location.value.id);
        closureRange.value = null;
        closureDate.value = null;
        Message.success("Closure added");
    } catch (error) {
        fail(error, "Failed to add closure");
    } finally {
        savingClosure.value = false;
    }
};

const removeClosure = async (row) => {
    try {
        await deleteClosure(location.value.id, row.id);
        closures.value = closures.value.filter((item) => item.id !== row.id);
        Message.success("Closure removed");
    } catch (error) {
        fail(error, "Failed to remove closure");
    }
};

watch(location, (selected) => {
    if (selected?.id) {
        loadSchedule(selected.id);
    }
});

onMounted(async () => {
    try {
        locations.value = await getUserLocationsSimple();
        location.value = locations.value[0] || null;
    } catch {
        Message.error("Failed to load locations");
    } finally {
        locationsLoading.value = false;
    }
});
</script>
